import React, { useState, useEffect, useCallback, useRef } from "react";

// ─────────────────────────────────────────────────────────────
// Utility: simple session hook stub (replace with your real one)
// ─────────────────────────────────────────────────────────────
function useSession() {
  // Replace with your real useSession hook
  return {
    userid: localStorage.getItem("userid") || "1",
    role: localStorage.getItem("role") || "Retailer",
    token: localStorage.getItem("authToken") || "",
    walletBal: parseFloat(localStorage.getItem("walletBal") || "0"),
  };
}

// ─────────────────────────────────────────────────────────────
// API helpers
// ─────────────────────────────────────────────────────────────
const BASE = "https://pro.mokshiq.in/api";

async function apiPost(url, token, payload) {
  const fd = new FormData();
  Object.entries(payload).forEach(([k, v]) => fd.append(k, v));
  const res = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  return res.json();
}

// ─────────────────────────────────────────────────────────────
// Role helpers
// ─────────────────────────────────────────────────────────────
const ROLE_KEY = (role) => role?.toLowerCase().replace(/ /g, "_") ?? "";

const ROLE_COLORS = {
  retailer:          { bg: "#ede9fe", color: "#5b21b6" },
  distributor:       { bg: "#e0f2fe", color: "#0369a1" },
  master_distributor:{ bg: "#dcfce7", color: "#15803d" },
  white_label:       { bg: "#fef9c3", color: "#a16207" },
  admin:             { bg: "#fce7f3", color: "#be185d" },
  main_admin:        { bg: "#f3e8ff", color: "#7e22ce" },
};

function roleBadgeStyle(rk) {
  const c = ROLE_COLORS[rk] || { bg: "#f3f4f6", color: "#4b5563" };
  return {
    display: "inline-flex", alignItems: "center", gap: 5,
    padding: "4px 11px", borderRadius: 20, fontSize: ".72rem",
    fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em",
    background: c.bg, color: c.color,
  };
}

// ─────────────────────────────────────────────────────────────
// Status Pill
// ─────────────────────────────────────────────────────────────
const STATUS_MAP = {
  pending: { bg: "#fef3c7", color: "#d97706", label: "Pending" },
  success: { bg: "#dcfce7", color: "#16a34a", label: "Approved" },
  approve: { bg: "#dcfce7", color: "#16a34a", label: "Approved" },
  failed:  { bg: "#fee2e2", color: "#dc2626", label: "Rejected" },
  reject:  { bg: "#fee2e2", color: "#dc2626", label: "Rejected" },
  Failed:  { bg: "#fee2e2", color: "#dc2626", label: "Rejected" },
};

function StatusPill({ status }) {
  const s = STATUS_MAP[status] || { bg: "#f3f4f6", color: "#4b5563", label: status };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 10px", borderRadius: 10, fontSize: ".72rem",
      fontWeight: 700, background: s.bg, color: s.color, whiteSpace: "nowrap",
    }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: "currentColor", flexShrink: 0 }} />
      {s.label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
// Toast
// ─────────────────────────────────────────────────────────────
function Toast({ toasts }) {
  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999, display: "flex", flexDirection: "column", gap: 8 }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          padding: "12px 20px", borderRadius: 10,
          background: t.type === "success" ? "#16a34a" : "#dc2626",
          color: "#fff", fontSize: ".875rem", fontWeight: 600,
          boxShadow: "0 4px 20px rgba(0,0,0,.18)",
          animation: "slideIn .25s ease",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          {t.type === "success" ? "✓" : "✕"} {t.message}
        </div>
      ))}
    </div>
  );
}

function useToast() {
  const [toasts, setToasts] = useState([]);
  const add = useCallback((message, type = "success") => {
    const id = Date.now();
    setToasts(p => [...p, { id, message, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 3200);
  }, []);
  return { toasts, add };
}

// ─────────────────────────────────────────────────────────────
// Modal Base
// ─────────────────────────────────────────────────────────────
function Modal({ show, onClose, title, children, footer }) {
  useEffect(() => {
    document.body.style.overflow = show ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [show]);

  if (!show) return null;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1050,
      background: "rgba(15,23,42,.45)", backdropFilter: "blur(2px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: "#fff", borderRadius: 14, width: "100%", maxWidth: 480,
        boxShadow: "0 20px 60px rgba(0,0,0,.2)",
        animation: "modalIn .2s ease",
        display: "flex", flexDirection: "column",
      }}>
        {/* header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 20px", borderBottom: "1px solid #e5e7eb",
          background: "#f9fafb", borderRadius: "14px 14px 0 0",
        }}>
          <span style={{ fontSize: ".95rem", fontWeight: 700, color: "#1f2937" }}>{title}</span>
          <button onClick={onClose} style={{
            background: "none", border: "none", cursor: "pointer",
            color: "#9ca3af", fontSize: 20, lineHeight: 1, padding: "2px 6px", borderRadius: 6,
          }}>×</button>
        </div>

        {/* body */}
        <div style={{ padding: "20px 20px 16px" }}>
          {children}
        </div>

        {/* footer */}
        {footer && (
          <div style={{
            padding: "13px 20px", background: "#f9fafb",
            borderTop: "1px solid #e5e7eb", borderRadius: "0 0 14px 14px",
            display: "flex", justifyContent: "flex-end", gap: 10,
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Target options config per role
// ─────────────────────────────────────────────────────────────
const TARGET_OPTS_BY_ROLE = {
  Retailer: [
    { value: "distributer",       label: "Distributor" },
    { value: "super_distributer", label: "Master Distributor" },
    { value: "white_label",       label: "Admin" },
   
  ],
  Distributor: [
    { value: "super_distributer", label: "Master Distributor" },
    { value: "white_label",       label: "Admmin" },
   
  ],
  "API Partner": [
    
    { value: "admin",             label: "Admin" },
  ],
  "Master Distributor": [
    { value: "white_label", label: "Admin" }
    
  ],
  "White label": [
    { value: "admin", label: "Admin" },
  ],
};


// Wallet type options per role
const WALLET_OPTS_BY_ROLE = (role) => {
  const rk = ROLE_KEY(role);
  const base = [
    { value: "main", label: "Main Wallet" },
    { value: "aeps", label: "AEPS Wallet" },
  ];
  if (rk === "white_label" || rk === "admin") {
    base.push({ value: "verification", label: "Verification Wallet" });
  }
  return base;
};
// ─────────────────────────────────────────────────────────────
// MODAL 1 — Raise Request
// ─────────────────────────────────────────────────────────────
function RaiseRequestModal({ show, onClose, role, userid, token, onSuccess }) {
  const opts = TARGET_OPTS_BY_ROLE[role] || [];
  const walletOpts = WALLET_OPTS_BY_ROLE(role);

  const [target, setTarget] = useState(opts[0]?.value || "");
  const [walletType, setWalletType] = useState("main");
  const [amount, setAmount] = useState("");
  const [utr, setUtr] = useState("");
  const [remark, setRemark] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setTarget(opts[0]?.value || "");
      setWalletType("main");
      setAmount(""); setUtr(""); setRemark(""); setErr("");
    }
  }, [show]);

  async function handleSubmit() {
    setErr("");
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0)
      return setErr("Please enter a valid amount.");
    if (!target)
      return setErr("Please select who to request from.");

    setLoading(true);
    try {
      const data = await apiPost(`${BASE}/wallet/load/request/`, token, {
        userid,
        target_fk: target,
        amount,
        wallet_type: walletType,
        remark: (utr ? `UTR:${utr} ` : "") + (remark || ""),
      });
      if (data.status) {
        onSuccess(`Request submitted! Ref: ${data.reference_num}`);
      } else {
        setErr(data.message || "Something went wrong.");
      }
    } catch {
      setErr("Network error. Please try again.");
    }
    setLoading(false);
  }

  return (
    <Modal show={show} onClose={onClose} title="New Wallet Load Request"
      footer={<>
        <Btn ghost onClick={onClose}>Cancel</Btn>
        <Btn primary onClick={handleSubmit} disabled={loading}>
          {loading ? "Please wait…" : "Submit Request"}
        </Btn>
      </>}
    >
      {/* Target selector */}
      <FieldWrap label="Request from">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>
          {opts.map(o => (
            <label key={o.value} onClick={() => setTarget(o.value)} style={{
              display: "flex", alignItems: "center", gap: 6, padding: "7px 15px",
              border: `1.5px solid ${target === o.value ? "#2563eb" : "#e5e7eb"}`,
              borderRadius: 20, fontSize: ".8rem", fontWeight: 600, cursor: "pointer",
              background: target === o.value ? "#dbeafe" : "#fff",
              color: target === o.value ? "#1d4ed8" : "#4b5563",
              transition: "all .15s",
            }}>
              <input type="radio" name="target_fk" value={o.value} checked={target === o.value}
                onChange={() => setTarget(o.value)} style={{ display: "none" }} />
              {o.label}
            </label>
          ))}
        </div>
      </FieldWrap>

      {/* Wallet type selector */}
      <FieldWrap label="Wallet to Load">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>
          {walletOpts.map(o => (
            <label key={o.value} onClick={() => setWalletType(o.value)} style={{
              display: "flex", alignItems: "center", gap: 6, padding: "7px 15px",
              border: `1.5px solid ${walletType === o.value ? "#7c3aed" : "#e5e7eb"}`,
              borderRadius: 20, fontSize: ".8rem", fontWeight: 600, cursor: "pointer",
              background: walletType === o.value ? "#ede9fe" : "#fff",
              color: walletType === o.value ? "#5b21b6" : "#4b5563",
              transition: "all .15s",
            }}>
              <input type="radio" name="wallet_type" value={o.value} checked={walletType === o.value}
                onChange={() => setWalletType(o.value)} style={{ display: "none" }} />
              {o.label}
            </label>
          ))}
        </div>
      </FieldWrap>

      {/* Amount + UTR */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 4 }}>
        <FieldWrap label="Amount (₹)">
          <Input type="number" placeholder="0.00" value={amount} onChange={e => setAmount(e.target.value)} min="1" />
        </FieldWrap>
        <FieldWrap label="UTR / Ref No.">
          <Input placeholder="Transaction reference" value={utr} onChange={e => setUtr(e.target.value)} />
        </FieldWrap>
      </div>

      <FieldWrap label="Remark (optional)">
        <Input placeholder="Any note for the approver" value={remark} onChange={e => setRemark(e.target.value)} />
      </FieldWrap>

      {err && <AlertBox type="danger">{err}</AlertBox>}
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────
// MODAL 2 — Admin Top-up (Admin → Main Admin)
// ─────────────────────────────────────────────────────────────
function AdminTopupModal({ show, onClose, userid, token, onSuccess }) {
  const walletOpts = WALLET_OPTS_BY_ROLE("Admin");

  const [walletType, setWalletType] = useState("main");
  const [amount, setAmount] = useState("");
  const [remark, setRemark] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) { setWalletType("main"); setAmount(""); setRemark(""); setErr(""); }
  }, [show]);

  async function handleSubmit() {
    setErr("");
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0)
      return setErr("Please enter a valid amount.");
    setLoading(true);
    try {
      const data = await apiPost(`${BASE}/wallet/topup/mainadmin/`, token, {
        action: "request", userid, amount,
        wallet_type: walletType,
        remark: remark || "",
      });
      if (data.status) {
        onSuccess(`Top-up request sent! Ref: ${data.reference_num}`);
      } else {
        setErr(data.message || "Something went wrong.");
      }
    } catch {
      setErr("Network error. Please try again.");
    }
    setLoading(false);
  }

  return (
    <Modal show={show} onClose={onClose} title="Request Top-up from Main Admin"
      footer={<>
        <Btn ghost onClick={onClose}>Cancel</Btn>
        <Btn primary onClick={handleSubmit} disabled={loading}>
          {loading ? "Please wait…" : "Send Request"}
        </Btn>
      </>}
    >
      {/* Wallet type selector */}
      <FieldWrap label="Wallet to Load">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>
          {walletOpts.map(o => (
            <label key={o.value} onClick={() => setWalletType(o.value)} style={{
              display: "flex", alignItems: "center", gap: 6, padding: "7px 15px",
              border: `1.5px solid ${walletType === o.value ? "#7c3aed" : "#e5e7eb"}`,
              borderRadius: 20, fontSize: ".8rem", fontWeight: 600, cursor: "pointer",
              background: walletType === o.value ? "#ede9fe" : "#fff",
              color: walletType === o.value ? "#5b21b6" : "#4b5563",
              transition: "all .15s",
            }}>
              <input type="radio" name="admin_wallet_type" value={o.value}
                checked={walletType === o.value}
                onChange={() => setWalletType(o.value)} style={{ display: "none" }} />
              {o.label}
            </label>
          ))}
        </div>
      </FieldWrap>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <FieldWrap label="Amount (₹)">
          <Input type="number" placeholder="0.00" value={amount} onChange={e => setAmount(e.target.value)} min="1" />
        </FieldWrap>
        <FieldWrap label="Remark / Note">
          <Input placeholder="Optional note" value={remark} onChange={e => setRemark(e.target.value)} />
        </FieldWrap>
      </div>
      {err && <AlertBox type="danger">{err}</AlertBox>}
    </Modal>
  );
}
// ─────────────────────────────────────────────────────────────
// MODAL 3 — Review / Approve / Reject
// ─────────────────────────────────────────────────────────────
function ReviewModal({ show, onClose, report, role, userid, token, onSuccess }) {
  const [decision, setDecision] = useState("");
  const [remark, setRemark] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) { setDecision(""); setRemark(""); setErr(""); }
  }, [show]);

  async function handleConfirm() {
    setErr("");
    if (!decision) return setErr("Please select Approve or Reject.");
    setLoading(true);

    const isMainAdmin = role === "Main Admin";
    const isTopup = report?.r_type === "main_admin_topup_request";

    let url, payload;
    if (decision === "approve") {
      if (isMainAdmin || isTopup) {
        url = `${BASE}/wallet/topup/mainadmin/`;
        payload = { action: "approve", main_admin_id: userid, report_id: report.id, remark: remark || "" };
      } else {
        url = `${BASE}/wallet/load/approve`;
        payload = { approver_id: userid, report_id: report.id };
      }
    } else {
      if (isMainAdmin || isTopup) {
        url = `${BASE}/wallet/topup/mainadmin/`;
        payload = { action: "reject", main_admin_id: userid, report_id: report.id, remark: remark || "Rejected" };
      } else {
        url = `${BASE}/wallet/load/reject/`;
        payload = { approver_id: userid, report_id: report.id, remark: remark || "Rejected" };
      }
    }

    try {
      const data = await apiPost(url, token, payload);
      if (data.status) {
        onSuccess(decision === "approve" ? "Request approved! Wallet updated." : "Request rejected.");
      } else {
        setErr(data.message || "Something went wrong.");
      }
    } catch {
      setErr("Network error. Please try again.");
    }
    setLoading(false);
  }

  if (!report) return null;

  return (
    <Modal show={show} onClose={onClose} title="Review Wallet Load Request"
      footer={<>
        <Btn ghost onClick={onClose}>Cancel</Btn>
        <Btn primary onClick={handleConfirm} disabled={loading}>
          {loading ? "Please wait…" : "Confirm Decision"}
        </Btn>
      </>}
    >
      {/* Summary card */}
      <div style={{
        background: "#f9fafb", borderRadius: 8, padding: "13px 15px", marginBottom: 16, fontSize: ".86rem",
      }}>
      {[
        ["Requester", report.requester_phone || report.requester_name || "—"],
        ["Role",      (report.requester_role || "—").replace(/_/g, " ")],
        ["Requested to", report.target_label || "—"],
        ["Wallet",    report.wallet_type || "Main Wallet"],   // ← add this line
        ["Amount",    `₹${report.amount}`],
      ].map(([label, val], i) =>(
          <div key={i} style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: i === 0 ? "3px 0" : "7px 0",
            borderTop: i > 0 ? "1px solid #e5e7eb" : "none",
          }}>
            <span style={{ color: "#9ca3af", fontSize: ".78rem" }}>{label}</span>
            <span style={{
              fontWeight: 700,
              ...(label === "Amount" ? { color: "#2563eb", fontSize: "1.1rem" } : {}),
            }}>{val}</span>
          </div>
        ))}
      </div>

      {/* Decision toggle */}
      <FieldWrap label="Your Decision">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 4 }}>
          {[
            { value: "approve", label: "Approve", icon: "✓", selBg: "#dcfce7", selColor: "#16a34a" },
            { value: "reject",  label: "Reject",  icon: "✕", selBg: "#fee2e2", selColor: "#dc2626" },
          ].map(opt => (
            <label key={opt.value} onClick={() => setDecision(opt.value)} style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
              padding: 10, borderRadius: 8, cursor: "pointer", transition: "all .15s",
              border: `1.5px solid ${decision === opt.value ? opt.selColor : "#e5e7eb"}`,
              background: decision === opt.value ? opt.selBg : "#fff",
              color: decision === opt.value ? opt.selColor : "#4b5563",
              fontWeight: 600, fontSize: ".84rem",
            }}>
              <input type="radio" name="rv_dec" value={opt.value} checked={decision === opt.value}
                onChange={() => setDecision(opt.value)} style={{ display: "none" }} />
              <span style={{ fontSize: 14 }}>{opt.icon}</span>
              {opt.label}
            </label>
          ))}
        </div>
      </FieldWrap>

      <FieldWrap label="Remark (optional)">
        <Input placeholder="Note for requester" value={remark} onChange={e => setRemark(e.target.value)} />
      </FieldWrap>

      {err && <AlertBox type="danger">{err}</AlertBox>}
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────
// Tiny design primitives
// ─────────────────────────────────────────────────────────────
function Btn({ primary, ghost, danger, success, children, onClick, disabled, sm }) {
  const base = {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: sm ? "5px 12px" : "8px 18px",
    borderRadius: 6, fontSize: sm ? ".78rem" : ".83rem",
    fontWeight: 600, border: "none", cursor: disabled ? "not-allowed" : "pointer",
    transition: "all .15s", opacity: disabled ? .65 : 1,
    textDecoration: "none",
  };
  const variant = primary  ? { background: "#2563eb", color: "#fff" }
    : ghost   ? { background: "#f3f4f6", color: "#1f2937" }
    : danger  ? { background: "#dc2626", color: "#fff" }
    : success ? { background: "#16a34a", color: "#fff" }
    : { background: "#f3f4f6", color: "#1f2937" };

  return <button style={{ ...base, ...variant }} onClick={onClick} disabled={disabled}>{children}</button>;
}
// ─────────────────────────────────────────────────────────────
// Static payment details — mirrors the Flutter QrViewerModal / AccountDetailsModal
// ─────────────────────────────────────────────────────────────
const kUpiId = "rakleservice@kotak";
const kBankAccountNumber = "1546778224";
const kBankIfsc = "KKBK0005206";
const kBankName = "Kotak Mahindra Bank";
const kAccountName = "Rakle Services Private Limited";
 
async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
 
// ─────────────────────────────────────────────────────────────
// Copyable row used inside the Bank Details modal
// ─────────────────────────────────────────────────────────────
function AccountRow({ label, value, icon, mono, highlighted }) {
  const [copied, setCopied] = useState(false);
 
  async function handleCopy() {
    const ok = await copyToClipboard(value);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }
 
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "12px 14px", borderRadius: 12,
      background: highlighted ? "#ebf0ff" : "#f8fafc",
      border: `1px solid ${highlighted ? "rgba(26,86,219,.25)" : "#e8edf5"}`,
    }}>
      <div style={{
        width: 30, height: 30, borderRadius: 8, flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: highlighted ? "rgba(26,86,219,.12)" : "#fff",
        border: "1px solid #e8edf5", fontSize: 14,
      }}>{icon}</div>
 
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: ".625rem", color: "#94a3b8", fontWeight: 600,
          letterSpacing: ".03em", textTransform: "uppercase",
        }}>{label}</div>
        <div style={{
          fontSize: highlighted ? "1rem" : ".82rem", fontWeight: 800,
          color: highlighted ? "#1a56db" : "#0f172a",
          fontFamily: mono ? "monospace" : "inherit",
          letterSpacing: mono ? ".3px" : 0,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{value}</div>
      </div>
 
      <button onClick={handleCopy} style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "6px 10px", borderRadius: 8, border: "none", cursor: "pointer",
        fontSize: ".69rem", fontWeight: 700, color: "#fff",
        background: copied ? "#15803d" : (highlighted ? "#1a56db" : "#374151"),
        transition: "background .2s",
      }}>
        {copied ? "✓ Copied!" : "⧉ Copy"}
      </button>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────
// MODAL — Show QR (UPI)
// ─────────────────────────────────────────────────────────────
function QrViewerModal({ show, onClose }) {
  const [copied, setCopied] = useState(false);
 
  async function handleCopyUpi() {
    const ok = await copyToClipboard(kUpiId);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }
 
  return (
    <Modal
      show={show}
      onClose={onClose}
      title="Payment QR Code"
      footer={<Btn ghost onClick={onClose}>Close</Btn>}
    >
      <div style={{ textAlign: "center" }}>
        <div style={{
          display: "inline-block", padding: 6, borderRadius: 18,
          background: "linear-gradient(135deg,#1a56db,#5b21b6)",
        }}>
          <div style={{ background: "#fff", borderRadius: 14, padding: 16 }}>
            <img
              src="../assets/images/logo/qr.jpg"
              alt="Payment QR"
              width={200}
              height={200}
              style={{ display: "block", objectFit: "contain" }}
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
            <div style={{
              display: "none", width: 200, height: 200, borderRadius: 10,
              background: "#ebf0ff", flexDirection: "column",
              alignItems: "center", justifyContent: "center", color: "#1a56db",
              fontSize: ".72rem", textAlign: "center", padding: 12,
            }}>
              QR not found
              <br />
              Check /assets/images/qr.jpg
            </div>
          </div>
        </div>
 
        <div style={{ fontSize: ".72rem", color: "#94a3b8", fontWeight: 600, margin: "12px 0" }}>
          Scan with any UPI app
        </div>
 
        {/* UPI ID row */}
        <div style={{
          display: "flex", alignItems: "center", gap: 10, textAlign: "left",
          padding: "12px 14px", borderRadius: 12, background: "#ebf0ff",
          border: "1px solid rgba(26,86,219,.2)",
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: ".625rem", color: "#94a3b8", fontWeight: 600,
              letterSpacing: ".03em", textTransform: "uppercase",
            }}>UPI ID</div>
            <div style={{ fontSize: ".9rem", fontWeight: 800, color: "#0f172a", letterSpacing: ".3px" }}>
              {kUpiId}
            </div>
          </div>
          <button onClick={handleCopyUpi} style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            padding: "7px 12px", borderRadius: 8, border: "none", cursor: "pointer",
            fontSize: ".72rem", fontWeight: 700, color: "#fff",
            background: copied ? "#15803d" : "#1a56db",
          }}>
            {copied ? "✓ Copied!" : "⧉ Copy"}
          </button>
        </div>
 
        <div style={{
          marginTop: 14, padding: "10px 14px", borderRadius: 10,
          background: "#fffbeb", border: "1px solid #fcd34d",
          fontSize: ".73rem", color: "#92400e", textAlign: "left", lineHeight: 1.5,
        }}>
          ℹ After payment, click <strong>New Request</strong> and enter your UTR / Ref No.
        </div>
      </div>
    </Modal>
  );
}
 
// ─────────────────────────────────────────────────────────────
// MODAL — Bank Account Details
// ─────────────────────────────────────────────────────────────
function AccountDetailsModal({ show, onClose }) {
  return (
    <Modal
      show={show}
      onClose={onClose}
      title="Bank Account Details"
      footer={<Btn ghost onClick={onClose}>Close</Btn>}
    >
      <div style={{
        display: "flex", alignItems: "center", gap: 8, padding: "10px 14px",
        borderRadius: 10, background: "#eff6ff", border: "1px solid #bfdbfe",
        marginBottom: 14, fontSize: ".82rem", fontWeight: 700, color: "#1d4ed8",
      }}>
        🏦 {kBankName}
      </div>
 
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <AccountRow label="Account Name" value={kAccountName} icon="👤" />
        <AccountRow label="Account Number" value={kBankAccountNumber} icon="#" mono highlighted />
        <AccountRow label="IFSC Code" value={kBankIfsc} icon="⌘" mono />
      </div>
 
      <div style={{
        marginTop: 14, padding: "10px 14px", borderRadius: 10,
        background: "#fffbeb", border: "1px solid #fcd34d",
        fontSize: ".73rem", color: "#92400e", lineHeight: 1.5,
      }}>
        ℹ After transferring, click <strong>New Request</strong> and enter your UTR / Ref No. so we can credit your wallet.
      </div>
    </Modal>
  );
}

function FieldWrap({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{
        display: "block", fontSize: ".74rem", fontWeight: 700, color: "#9ca3af",
        textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 5,
      }}>{label}</label>
      {children}
    </div>
  );
}

function Input({ value, onChange, type = "text", placeholder, min }) {
  return (
    <input
      type={type} value={value} onChange={onChange} placeholder={placeholder} min={min}
      style={{
        width: "100%", border: "1.5px solid #e5e7eb", borderRadius: 6,
        fontSize: ".875rem", padding: "9px 12px", outline: "none",
        transition: "border-color .15s",
        boxSizing: "border-box",
      }}
      onFocus={e => e.target.style.borderColor = "#2563eb"}
      onBlur={e => e.target.style.borderColor = "#e5e7eb"}
    />
  );
}

function AlertBox({ type, children }) {
  const c = type === "danger"
    ? { bg: "#fee2e2", color: "#dc2626", border: "#dc2626" }
    : { bg: "#dcfce7", color: "#16a34a", border: "#16a34a" };
  return (
    <div style={{
      padding: "9px 13px", borderRadius: 6, fontSize: ".83rem", marginTop: 12,
      display: "flex", alignItems: "center", gap: 8,
      background: c.bg, color: c.color, borderLeft: `3px solid ${c.border}`,
    }}>
      {type === "danger" ? "⚠" : "✓"} {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// formatDate
// ─────────────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    + ", " + dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
}

// ─────────────────────────────────────────────────────────────
// TABLE
// ─────────────────────────────────────────────────────────────
function Table({ rows, role, userId, onReview, canApprove = true }) {
  const rk = ROLE_KEY(role);
  const isApprover = ["distributor","master_distributor","white_label","admin","main_admin","platform_user"].includes(rk);

  function isOwnRequest(item) {
    const uid = String(userId);
    if (item.requester_user_id != null)  return String(item.requester_user_id) === uid;
    if (item.user_id != null)            return String(item.user_id) === uid;
    return item.requester_phone === userId;
  }

  if (!rows?.length) {
    return (
      <div style={{ textAlign: "center", padding: "56px 20px", color: "#9ca3af" }}>
        <div style={{ fontSize: "2.8rem", marginBottom: 10, opacity: .35 }}>📭</div>
        <div style={{ fontSize: ".875rem" }}>No wallet load requests yet.</div>
      </div>
    );
  }

  const th = {
    background: "#f9fafb", color: "#9ca3af", fontSize: ".71rem", fontWeight: 700,
    textTransform: "uppercase", letterSpacing: ".06em",
    padding: "10px 16px", borderBottom: "1px solid #e5e7eb",
    whiteSpace: "nowrap", textAlign: "left",
  };
  const td = {
    padding: "13px 16px", borderBottom: "1px solid #f3f4f6",
    color: "#1f2937", verticalAlign: "middle", fontSize: ".855rem",
  };

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".855rem" }}>
        <thead>
          <tr>
            <th style={th}>#</th>
            <th style={th}>Ref / Date</th>
            <th style={th}>Amount</th>
            <th style={th}>Status</th>
            <th style={th}>Wallet</th>
            {isApprover && <th style={th}>Type</th>}
            <th style={th}>Remark</th>
            {isApprover ? <>
              <th style={th}>Requester</th>
              <th style={th}>Requested To</th>
              <th style={th}>Balance (open→close)</th>
              <th style={th}>Action</th>
            </> : <>
              <th style={th}>Requested To</th>
              <th style={th}>Balance</th>
            </>}
          </tr>
        </thead>
        <tbody>
          {rows.map((item, idx) => {
            const isOwnRow = isOwnRequest(item);
            return (
              <tr key={item.id || idx}
                style={{ background: isOwnRow ? "#f0f9ff" : "transparent" }}
                onMouseEnter={e => !isOwnRow && (e.currentTarget.style.background = "#f9fafb")}
                onMouseLeave={e => !isOwnRow && (e.currentTarget.style.background = "transparent")}
              >
                <td style={{ ...td, color: "#9ca3af", fontSize: ".75rem" }}>{item.id}</td>

                <td style={td}>
                  <span style={{ fontWeight: 600 }}>{item.ref_id || "—"}</span>
                  <span style={{ display: "block", fontSize: ".72rem", color: "#9ca3af", marginTop: 2 }}>
                    {fmtDate(item.created_at)}
                  </span>
                </td>

                <td style={td}>
                  <strong style={{ fontSize: ".95rem" }}>₹{item.amount}</strong>
                </td>

                <td style={td}><StatusPill status={item.status} /></td>
                <td style={td}>
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 4,
    fontSize: ".72rem", fontWeight: 600,
    background: "#ede9fe", color: "#5b21b6",
    padding: "2px 9px", borderRadius: 8, whiteSpace: "nowrap",
  }}>
    {item.wallet_type || "Main Wallet"}
  </span>
</td>
                {isApprover && (
                  <td style={td}>
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 4,
                      fontSize: ".72rem", fontWeight: 600,
                      background: "#f3f4f6", color: "#4b5563",
                      padding: "2px 9px", borderRadius: 8,
                    }}>
                      {item.r_type === "main_admin_topup_request" ? "Top-up" : "Load"}
                    </span>
                  </td>
                )}

                <td style={{ ...td, maxWidth: 140, fontSize: ".8rem", color: "#4b5563" }}>
                  {(item.description || item.remark || "—").slice(0, 50)}
                  {(item.description || item.remark || "").length > 50 ? "…" : ""}
                </td>

                {isApprover ? <>
                  <td style={td}>
                    {isOwnRow
                      ? <span style={{ fontSize: ".78rem", color: "#9ca3af" }}>(My request)</span>
                      : <>
                        <span style={{ fontWeight: 600 }}>{item.requester_phone || item.requester_name}</span>
                        <span style={{ display: "block", fontSize: ".72rem", color: "#9ca3af", marginTop: 2 }}>
                          {(item.requester_role || "").replace(/_/g, " ")}
                        </span>
                      </>
                    }
                  </td>

                  <td style={td}>
                    {item.target_label
                      ? <span style={{
                          display: "inline-flex", alignItems: "center", gap: 4,
                          fontSize: ".72rem", fontWeight: 600,
                          background: "#f3f4f6", color: "#4b5563",
                          padding: "2px 9px", borderRadius: 8,
                        }}>
                          👤 {item.target_label}
                        </span>
                      : "—"}
                  </td>

                  <td style={{ ...td, fontSize: ".78rem", color: "#4b5563" }}>
                    {item.opening_balance ? `₹${item.opening_balance}` : "—"}
                    {item.closing_balance && (
                      <> → <span style={{ color: "#16a34a", fontWeight: 600 }}>₹{item.closing_balance}</span></>
                    )}
                  </td>
                  <td style={td}>
                    {item.status === "pending" && !isOwnRow
                      ? (canApprove
                          ? <Btn primary sm onClick={() => onReview(item)}>👁 Review</Btn>
                          : <span style={{ fontSize: ".75rem", color: "#9ca3af" }}>Not authorized</span>)
                      : item.status === "pending"
                        ? <span style={{ fontSize: ".75rem", color: "#9ca3af" }}>Awaiting approval</span>
                        : <span style={{ fontSize: ".75rem", color: "#9ca3af" }}>—</span>
                    }
                  </td>
                </> : <>
                  <td style={td}>
                    {item.target_label
                      ? <span style={{
                          display: "inline-flex", gap: 4, fontSize: ".72rem", fontWeight: 600,
                          background: "#f3f4f6", color: "#4b5563", padding: "2px 9px", borderRadius: 8,
                        }}>{item.target_label}</span>
                      : "—"}
                  </td>
                  <td style={{ ...td, fontSize: ".78rem", color: "#4b5563" }}>
                    {item.opening_balance ? `₹${item.opening_balance}` : "—"}
                    {item.closing_balance && (
                      <> → <span style={{ color: "#16a34a", fontWeight: 600 }}>₹{item.closing_balance}</span></>
                    )}
                  </td>
                </>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────
export default function LoadWalletPage() {
  const { userid, role, token, walletBal } = useSession();
  const rk = ROLE_KEY(role);
  console.log("DEBUG role:", JSON.stringify(role)); // ← tem
  const isPlatformUser = role === "Platform User";

  const [rows, setRows] = useState([]);
  const [walletBalance, setWalletBalance] = useState(walletBal);
  const [loading, setLoading] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [showBankDetails, setShowBankDetails] = useState(false);

  // Modal state
  const [showRaise, setShowRaise] = useState(false);
  const [showTopup, setShowTopup] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  // ── Platform User permission flags (fetch once) ──────────────────
  const [puModules, setPuModules] = useState({ can_credit: false, can_debit: false });
  const [puLoaded, setPuLoaded] = useState(!isPlatformUser); // non-PU users don't need to wait

  useEffect(() => {
    if (!isPlatformUser) return;
    const fd = new FormData();
    fd.append("action", "GET_MY_MODULES");
    fetch("https://pro.mokshiq.in/platform_user_action", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    })
      .then(res => res.json())
      .then(data => {
        console.log("PU modules response:", data); // ← temp debug
        if (data.status) setPuModules(data.data || {});
      })
      .catch(() => {})
      .finally(() => setPuLoaded(true));
  }, [isPlatformUser, token]);

  // Platform User can approve/reject only if they hold can_debit
  // (Non-platform-user approver roles are always allowed — unchanged)
  const canApprove = !isPlatformUser || puModules.can_debit;

  const { toasts, add: addToast } = useToast();

  // ── Fetch data ──
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("userid", userid);
      const res = await fetch(`https://pro.mokshiq.in/load_wallet_amount`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (data.status) {
        setRows(data.data || []);
        setWalletBalance(data.wallet_bal ?? walletBal);
      }
    } catch {
      addToast("Failed to load data", "error");
    }
    setLoading(false);
  }, [userid, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Handlers ──
  function handleSuccess(msg) {
    addToast(msg, "success");
    setShowRaise(false);
    setShowTopup(false);
    setShowReview(false);
    setTimeout(fetchData, 600);
  }

  function openReview(report) {
    setSelectedReport(report);
    setShowReview(true);
  }

  // ── Table heading ──
  const tableTitle = isPlatformUser ? "Wallet Load Requests (Approve/Reject)"
    : role === "Main Admin" ? "Admin Top-up Requests"
    : role === "Admin" ? "Incoming Requests & My Top-up Requests"
    : role === "Retailer" ? "My Wallet Load Requests"
    : "Wallet Load Requests";

  return (
    <div style={{ padding: "28px 0 48px", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <style>{`
        @keyframes slideIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes modalIn { from { opacity: 0; transform: scale(.96) translateY(12px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        * { box-sizing: border-box; }
      `}</style>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 20px" }}>

        {/* ── Page header ── */}
        <div style={{
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          flexWrap: "wrap", gap: 12, marginBottom: 24,
        }}>
          <div>
            <h4 style={{ fontSize: "1.25rem", fontWeight: 700, color: "#1f2937", margin: "0 0 3px" }}>
              Load Wallet
            </h4>
            <div style={{ fontSize: ".78rem", color: "#9ca3af" }}>
              <span style={{ color: "#9ca3af", textDecoration: "none" }}>Home</span>
              {" ›"} Load Wallet
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {/* Wallet balance chip */}
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: "#dbeafe", color: "#1d4ed8",
              padding: "7px 14px", borderRadius: 20, fontSize: ".82rem", fontWeight: 600,
            }}>
              💳 ₹{parseFloat(walletBalance || 0).toFixed(2)}
            </div>

            {/* Role badge */}
            <span style={roleBadgeStyle(rk)}>{role}</span>


            <Btn ghost onClick={() => setShowQr(true)}>⬜ Show QR</Btn>
            <Btn ghost onClick={() => setShowBankDetails(true)}>🏦 Bank Details</Btn>

            {/* Action button — NOT shown for Platform User (view/approve only) */}
            {!isPlatformUser && (role === "Retailer" || role === "Distributor" || role === "Master Distributor" || role === "White label" || role === "API Partner") && (
              <Btn primary onClick={() => setShowRaise(true)}>
                + New Request
              </Btn>
            )}
            {!isPlatformUser && role === "Admin" && (
              <Btn primary onClick={() => setShowTopup(true)}>
                + Request Top-up
              </Btn>
            )}
            {/* Main Admin: no raise button */}
          </div>
        </div>

        {/* ── Retailer info box ── */}
        {role === "Retailer" && (
          <div style={{
            display: "flex", gap: 14, alignItems: "flex-start",
            background: "#dbeafe", border: "1.5px dashed #93c5fd",
            borderRadius: 12, padding: "18px 20px", marginBottom: 20,
          }}>
            <div style={{
              width: 34, height: 34, background: "#2563eb", borderRadius: "50%",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              fontSize: 16,
            }}>ℹ</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: ".875rem", color: "#1d4ed8", marginBottom: 3 }}>
                How to load your wallet
              </div>
              <div style={{ fontSize: ".8rem", color: "#1d4ed899", lineHeight: 1.55 }}>
                Click <strong>New Request</strong> to request wallet funds. You can request from your Distributor,
                Master Distributor, White Label, or Admin. Once approved the amount is instantly added to your wallet.
              </div>
            </div>
          </div>
        )}

        {/* ── Platform User info box ── */}
        {isPlatformUser && (
          <div style={{
            display: "flex", gap: 14, alignItems: "flex-start",
            background: "#fef9c3", border: "1.5px dashed #fde047",
            borderRadius: 12, padding: "18px 20px", marginBottom: 20,
          }}>
            <div style={{
              width: 34, height: 34, background: "#a16207", borderRadius: "50%",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              fontSize: 16, color: "#fff",
            }}>ℹ</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: ".875rem", color: "#a16207", marginBottom: 3 }}>
                Platform User Access
              </div>
              <div style={{ fontSize: ".8rem", color: "#a1650799", lineHeight: 1.55 }}>
                {puModules.can_debit
                  ? "You can review and approve/reject incoming wallet load requests on behalf of your account."
                  : "You have view-only access. Approving/rejecting requests requires debit permission — please contact your admin."}
              </div>
            </div>
          </div>
        )}

        {/* ── Table card ── */}
        <div style={{
          background: "#fff", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,.07)",
          border: "1px solid #e5e7eb", overflow: "hidden",
        }}>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexWrap: "wrap", gap: 8, padding: "15px 20px",
            borderBottom: "1px solid #e5e7eb", background: "#f9fafb",
          }}>
            <span style={{ fontSize: ".92rem", fontWeight: 700, color: "#1f2937" }}>
              {tableTitle}
            </span>
            <span style={{
              fontSize: ".75rem", color: "#9ca3af", background: "#f3f4f6",
              borderRadius: 10, padding: "2px 9px", fontWeight: 600,
            }}>
              {rows.length} record{rows.length !== 1 ? "s" : ""}
            </span>
          </div>

          {loading || (isPlatformUser && !puLoaded)
            ? <div style={{ padding: "56px 20px", textAlign: "center", color: "#9ca3af" }}>
                Loading…
              </div>
            : <Table rows={rows} role={role} userId={userid} onReview={openReview} canApprove={canApprove} />
          }
        </div>

      </div>

      {/* ── Modals ── */}
      {/* Raise/Topup modals still exist for non-platform-user roles */}
      <RaiseRequestModal
        show={showRaise}
        onClose={() => setShowRaise(false)}
        role={role}
        userid={userid}
        token={token}
        onSuccess={handleSuccess}
      />

      <AdminTopupModal
        show={showTopup}
        onClose={() => setShowTopup(false)}
        userid={userid}
        token={token}
        onSuccess={handleSuccess}
      />
      <QrViewerModal show={showQr} onClose={() => setShowQr(false)} />
      <AccountDetailsModal show={showBankDetails} onClose={() => setShowBankDetails(false)} />

      <ReviewModal
        show={showReview}
        onClose={() => setShowReview(false)}
        report={selectedReport}
        role={role}
        userid={userid}
        token={token}
        onSuccess={handleSuccess}
      />

      <Toast toasts={toasts} />
    </div>
  );
}